import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import ImageBand from "@/components/site/v2/ImageBand";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";
import { SOLUTIONS } from "@/data/solutions";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const CARD_COPY = {
  growth360: "Find what is holding growth back. Identify commercial constraints, quantify opportunities and prioritize the next moves that deserve investment.",
  profitshield: "Stop growth from leaking into costs. Find margin erosion across prices, discounts, products, customers and channels—and design a practical recovery plan.",
  salesmax: "Make the sales system work harder. Improve productivity, coverage, pipeline conversion, management routines and commercial execution.",
  channelmax: "Reach more customers at the right economics. Improve distribution coverage, partner performance, sell-through and channel profitability.",
  commercemax: "Grow online sales without losing sight of profit. Connect D2C and marketplace acquisition, conversion, order economics and retention.",
};

export default function SolutionsHubPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: "Focused Consulting Solutions — Growth360, ProfitShield, SalesMax, ChannelMax, CommerceMax | BLEZZINGS",
    description:
      "Five focused consulting engagements for profitable growth: business growth diagnostics, profit leakage recovery, sales productivity, distribution performance and profitable e-commerce.",
    canonical: "https://blezzings.co/solutions",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Solutions", url: "https://blezzings.co/solutions" },
    ],
  });

  return (
    <div data-testid="solutions-hub" className="relative bg-white">
      <Nav onBook={() => setBookOpen(true)} />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="pt-24 md:pt-28">
        <ol className={`${GRID} flex items-center gap-2 text-xs text-zinc-500`}>
          <li><Link to="/" className="hover:text-black transition-colors">Home</Link></li>
          <li aria-hidden>›</li>
          <li className="text-black font-medium" aria-current="page">Solutions</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="pt-10 md:pt-14 pb-14 md:pb-20 bg-white border-b border-black/10">
        <div className={`${GRID} max-w-4xl`}>
          <SectionBadge label="Focused Consulting Solutions" />
          <h1 className="font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.04] tracking-tight">
            Five focused solutions. One goal: profitable growth.
          </h1>
          <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-2xl">
            Some growth problems need more than another strategy presentation. BLEZZINGS
            combines commercial diagnosis, structured decision-making and hands-on execution
            support to address the constraints that matter most.
          </p>
          <p className="mt-4 text-sm text-zinc-500 max-w-2xl">
            Our solutions are focused engagements within BLEZZINGS' wider sales and marketing
            transformation capabilities.
          </p>
        </div>
      </section>

      {/* Five cards */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="solutions-cards">
        <div className={GRID}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SOLUTIONS.map((s, i) => (
              <Link
                key={s.id}
                to={`/solutions/${s.id}`}
                data-testid={`solution-card-${s.id}`}
                onClick={() => track("solution_card_click", { solution_id: s.id, source_page: "solutions-hub" })}
                className="group bg-white border border-zinc-200 p-6 hover:border-black transition-colors flex flex-col"
              >
                <div className="relative -mx-6 -mt-6 mb-5 aspect-[16/10] overflow-hidden bg-zinc-200">
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover img-edit transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">{String(i + 1).padStart(2, "0")}</div>
                <h2 className="font-serif-display font-bold text-xl mt-3 text-black leading-tight">{s.name}</h2>
                <div className="text-[11px] font-medium tracking-wide text-zinc-500 mt-1 leading-snug">{s.descriptor}</div>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">{CARD_COPY[s.id]}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black">
                  Explore solution
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Visual chapter break */}
      <ImageBand
        testid="solutions-band"
        image="/photos/expertise-strategy.jpg"
        alt="Indian strategy team reviewing growth plans on a glass planning wall"
        caption="BLEZZINGS Solutions"
      />

      {/* Three-part explainer */}
      <section className="py-14 md:py-20 bg-white">
        <div className={GRID}>
          <div className="overline">How It Works</div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
            {["Discover the problem", "Diagnose with evidence", "Design and activate improvements"].map((s, i) => (
              <div key={i} className="border-t-2 border-black pt-5 flex items-center gap-4 md:block" data-testid={`solutions-step-${i}`}>
                <div className="hidden md:block font-serif-display text-3xl text-zinc-200">{i + 1}</div>
                <span className="md:hidden inline-flex h-8 w-8 shrink-0 items-center justify-center bg-black text-white text-xs font-semibold">{i + 1}</span>
                <p className="text-[15px] font-semibold text-black md:mt-2">{s}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-zinc-500 max-w-2xl">
            Engagement scope and timeline depend on business complexity and data availability.
          </p>
        </div>
      </section>

      {/* Not sure */}
      <section className="py-14 md:py-20 bg-black text-white" data-testid="solutions-not-sure">
        <div className={`${GRID} max-w-3xl`}>
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl leading-tight">
            The right starting point is a conversation.
          </h2>
          <p className="mt-4 text-zinc-300 leading-relaxed">
            Tell us what is happening in your business. In a complimentary discovery
            conversation, we will clarify the decision you are facing and recommend an
            appropriate next step. A detailed, data-led diagnostic is separately scoped
            and normally a paid engagement.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact?solution=not-sure&source=solutions-hub"
              data-testid="solutions-hub-cta"
              onClick={() => track("consultation_cta_click", { solution_id: "not-sure", source_page: "solutions-hub" })}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
            >
              Discuss Your Growth Challenge <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/tools"
              data-testid="solutions-hub-tools"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 border border-white/40 text-white text-sm font-medium hover:bg-white hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
            >
              Try a Free Growth Tool
            </Link>
          </div>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => setBookOpen(true)} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
